import { returnData, apiClientVersioned } from '../index'

import type { IPaginatedResponse, IPaginationParams } from '../index'
import type {
  IAdminFeedbackListParams,
  IAdminUserListParams,
  IConfigValue,
  IFoundeverSystemPrompt,
  IGPTSystemPrompt,
  TFoundryConfigFlag,
} from '../types/admin'
import type { IAdminUser } from '../types/authUser'
import type { IMessageFeedback } from '../types/chat'
import type { IGpt, IGptAdminSummary, IGptListFilters } from '../types/gpt'
import type {
  ILLMModelAdmin,
  ILLMModelCircuitState,
  ILLMModelCreateBody,
  ILLMModelPatchBody,
} from '../types/llmModelAdmin'
import type {
  IMcpConnectorAdmin,
  IMcpConnectorAdminList,
  IMcpConnectorCreateBody,
  TMcpConnectorPatchBody,
} from '../types/mcpConnectorAdmin'
import type { IUsageDailyMetricsResponse } from '../types/metrics'

const adminLlmModelPath = (id: string) =>
  `/admin/llms/models/${encodeURIComponent(id)}`

// One request returns at most 10,000 users.
const ADMIN_USERS_CHUNK_SIZE = 5000

export const AdminApi = {
  // #region System Prompts
  /**
   * The global system prompt prepended to all regular chat conversations.
   * Defines EverGPT Turbo's core behavior, personality, and refusal rules.
   */
  getSystemPrompt: () =>
    returnData(
      apiClientVersioned.get<IFoundeverSystemPrompt>(`/config/system_prompt`),
    ),
  /**
   * Prompt template for auto-generating chat titles from conversation history.
   * Instructs the LLM to create concise, 3-5 word titles with emojis.
   */
  getTitleSystemPrompt: () =>
    returnData(
      apiClientVersioned.get<IFoundeverSystemPrompt>(
        `/config/title_system_prompt`,
      ),
    ),
  /**
   * Base system prompt prepended to custom GPT instructions.
   * Sets safety guardrails and core rules that custom GPT instructions cannot override.
   */
  getGPTSystemPrompt: () =>
    returnData(
      apiClientVersioned.get<IGPTSystemPrompt>(`/config/gpt_system_prompt`),
    ),
  /**
   * Template for personalizing AI interactions based on user context.
   * Dynamically adjusts the user's name, tone, role, and work context for tailored responses.
   */
  getPersonalizedSystemPrompt: () =>
    returnData(
      apiClientVersioned.get<IGPTSystemPrompt>(
        `/config/personalized_system_prompt`,
      ),
    ),

  updateSystemPrompt: (value: string) =>
    returnData(
      apiClientVersioned.post<IFoundeverSystemPrompt>(`/config/system_prompt`, {
        value,
      }),
    ),
  updateTitleSystemPrompt: (value: string) =>
    returnData(
      apiClientVersioned.post<IFoundeverSystemPrompt>(
        `/config/title_system_prompt`,
        {
          value,
        },
      ),
    ),
  updateGptSystemPrompt: (value: string) =>
    returnData(
      apiClientVersioned.post<IGPTSystemPrompt>(`/config/gpt_system_prompt`, {
        value,
      }),
    ),
  updatePersonalizedSystemPrompt: (value: string) =>
    returnData(
      apiClientVersioned.post<IFoundeverSystemPrompt>(
        `/config/personalized_system_prompt`,
        {
          value,
        },
      ),
    ),
  // #endregion

  // #region Users
  getUsers: (params?: IAdminUserListParams) =>
    returnData(
      apiClientVersioned.get<IPaginatedResponse<IAdminUser>>('/admin/users', {
        params,
      }),
    ),
  /** Every user, read in chunks and de-duplicated by id; complete when `items.length === total`. */
  getAllUsers: async (): Promise<
    Pick<IPaginatedResponse<IAdminUser>, 'items' | 'total'>
  > => {
    const usersById = new Map<string, IAdminUser>()
    let page = 1
    let usersPage: IPaginatedResponse<IAdminUser>

    do {
      usersPage = await AdminApi.getUsers({
        page,
        size: ADMIN_USERS_CHUNK_SIZE,
        // TODO(EVGPT-1920): sort oldest first once the API supports sort_by and order
        // sort_by: 'created_at',
        // order: 'asc',
      })
      for (const user of usersPage.items) usersById.set(user.id, user)
      page += 1
    } while (page <= usersPage.pages)

    return { items: [...usersById.values()], total: usersPage.total }
  },
  updateUserRole: (id: string, role: IAdminUser['role']) =>
    returnData(
      apiClientVersioned.patch<IAdminUser>(`/admin/users/${id}`, {
        role,
      }),
    ),
  /** Soft-deletes a user (reversible via restoreUser). Returns 204. */
  deleteUser: (id: string) =>
    returnData(apiClientVersioned.delete<void>(`/admin/users/${id}`)),
  /** Restores a soft-deleted user. Returns 204 (400 if the user is not deleted). */
  restoreUser: (id: string) =>
    returnData(apiClientVersioned.post<void>(`/admin/users/${id}/restore`)),
  syncTopDepartments: () =>
    returnData(apiClientVersioned.post('/users/sync/departments')),
  getGpts: (filters?: Partial<IGptListFilters>) =>
    returnData(
      apiClientVersioned.get<IPaginatedResponse<IGptAdminSummary>>(
        `/admin/gpts`,
        {
          params: filters,
          paramsSerializer: {
            indexes: null,
          },
        },
      ),
    ),

  // #region GPT
  getGpt: (id: string) =>
    returnData(apiClientVersioned.get<IGpt>(`/admin/gpts/${id}`)),
  updateGpt: (data: IGpt | Omit<IGpt, 'config'>) =>
    returnData(apiClientVersioned.put<IGpt>(`/admin/gpts/${data.id}`, data)),
  removeGpt: (id: string) =>
    returnData(apiClientVersioned.delete<IGpt>(`/admin/gpts/${id}`)),
  exportGpt: (gptIds?: string[]) => {
    const params = gptIds?.length ? { gpts: gptIds.join(',') } : {}
    return returnData(apiClientVersioned.get('/admin/gpts/export', { params }))
  },
  importGpt: (data: object) =>
    returnData(apiClientVersioned.post('/admin/gpts/import', data)),
  // #endregion

  // #region Feedback
  /** Message feedback from every user (thumbs + 1–10 rating + comment). Root only. */
  listFeedback: (params?: IAdminFeedbackListParams) =>
    returnData(
      apiClientVersioned.get<IPaginatedResponse<IMessageFeedback>>(
        '/admin/chats/feedback',
        { params },
      ),
    ),
  // #endregion

  // #region Published HTML
  unpublishPublishedHtml: (fileId: string) =>
    returnData(
      apiClientVersioned.delete<void>(`/admin/publishing/html/${fileId}`),
    ),
  // #endregion

  // #region Metrics
  getUsageDailyMetrics: (params: {
    start_date: string
    end_date: string
    employee_id?: string
    force_recalculate?: boolean
  }) =>
    returnData(
      apiClientVersioned.get<IUsageDailyMetricsResponse>(
        '/admin/metrics/usage/daily',
        { params },
      ),
    ),
  // #endregion

  // #region LLM models (per-model DB, EVGPT-1245)
  listLlmModels: (
    params?: IPaginationParams & {
      sort_by?: string
      order?: 'asc' | 'desc'
    },
  ) =>
    returnData(
      apiClientVersioned.get<IPaginatedResponse<ILLMModelAdmin>>(
        `/admin/llms/models`,
        { params },
      ),
    ),
  getLlmModel: (id: string) =>
    returnData(apiClientVersioned.get<ILLMModelAdmin>(adminLlmModelPath(id))),
  createLlmModel: (body: ILLMModelCreateBody) =>
    returnData(
      apiClientVersioned.post<ILLMModelAdmin>(`/admin/llms/models`, body),
    ),
  patchLlmModel: (id: string, body: ILLMModelPatchBody) =>
    returnData(
      apiClientVersioned.patch<ILLMModelAdmin>(adminLlmModelPath(id), body),
    ),
  deleteLlmModel: (id: string) =>
    returnData(apiClientVersioned.delete<void>(adminLlmModelPath(id))),
  getLlmModelCircuitState: (id: string) =>
    returnData(
      apiClientVersioned.get<ILLMModelCircuitState>(
        `${adminLlmModelPath(id)}/circuit-state`,
      ),
    ),
  resetLlmModelCircuitState: (id: string) =>
    returnData(
      apiClientVersioned.post<ILLMModelCircuitState>(
        `${adminLlmModelPath(id)}/circuit-state/reset`,
      ),
    ),
  // #endregion

  // #region LLM Foundry (EVGPT-1967)
  /** 404 while never saved; the API then defaults to `true`. */
  getFoundryFlag: (key: TFoundryConfigFlag) =>
    returnData(apiClientVersioned.get<IConfigValue>(`/config/${key}`)),
  /** Stored as `'true'` / `'false'`; API pods pick it up within 5 seconds. */
  updateFoundryFlag: (key: TFoundryConfigFlag, value: boolean) =>
    returnData(
      apiClientVersioned.post<IConfigValue>(`/config/${key}`, {
        value: String(value),
      }),
    ),
  // #endregion

  // #region MCP connectors
  /** Every catalog row, ordered by `sort_order`. Unpaginated. */
  listMcpConnectors: () =>
    returnData(apiClientVersioned.get<IMcpConnectorAdminList>('/admin/mcp')),
  createMcpConnector: (body: IMcpConnectorCreateBody) =>
    returnData(apiClientVersioned.post<IMcpConnectorAdmin>('/admin/mcp', body)),
  patchMcpConnector: (id: string, body: TMcpConnectorPatchBody) =>
    returnData(
      apiClientVersioned.patch<IMcpConnectorAdmin>(
        `/admin/mcp/${encodeURIComponent(id)}`,
        body,
      ),
    ),
  // #endregion
}
